import { oneSecondInMilliseconds } from "constants/time/oneSecondInMilliseconds";

import { useEffect, useState } from "react";

import { skipToken } from "@reduxjs/toolkit/dist/query/react";
import useDocumentVisibility from "hooks/useDocumentVisibility";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Sentry } from "sentry";
import { downloadBlobFromUrl } from "utils/downloadBlobFromUrl";

import {
  useExportConversationHistoryMutation,
  useGetConversationHistoryDownloadQuery,
} from "../api/downloadConversationHistory";

const CHAT_HISTORY_POLLING_INTERVAL_MS = oneSecondInMilliseconds * 5;

const useDownloadConversationHistory = (
  conversationId?: number | null
): {
  isDownloading: boolean;
  startDownload: () => Promise<void>;
} => {
  const { t } = useTranslation(["chat", "common"]);
  const { enqueueSnackbar } = useSnackbar();
  const isDocumentVisible = useDocumentVisibility();
  const [jobId, setJobId] = useState<string>();
  const [exportConversationHistory, exportConversationHistoryResult] = useExportConversationHistoryMutation();

  const downloadResult = useGetConversationHistoryDownloadQuery(
    conversationId && jobId ? { conversationId, jobId } : skipToken,
    {
      pollingInterval: isDocumentVisible ? CHAT_HISTORY_POLLING_INTERVAL_MS : undefined,
    }
  );

  useEffect(() => {
    if (!jobId || !downloadResult.error) {
      return;
    }

    enqueueSnackbar(t("chat:downloadHistory_error"), { variant: "error" });
    Sentry.captureException(downloadResult.error);
    setJobId(undefined);
  }, [downloadResult.error, enqueueSnackbar, jobId, t]);

  useEffect(() => {
    const downloadFile = async () => {
      if (!jobId || !downloadResult.data?.downloadUrl || !downloadResult.data.fileName) {
        return;
      }

      try {
        await downloadBlobFromUrl(downloadResult.data.downloadUrl, downloadResult.data.fileName);
      } catch (error) {
        enqueueSnackbar(t("chat:downloadHistory_error"), { variant: "error" });
        Sentry.captureException(error);
      } finally {
        setJobId(undefined);
      }
    };

    void downloadFile();
  }, [downloadResult.data, enqueueSnackbar, jobId, t]);

  const startDownload = async () => {
    if (!conversationId || exportConversationHistoryResult.isLoading || !!jobId) {
      return;
    }

    try {
      const response = await exportConversationHistory({ conversationId }).unwrap();
      setJobId(response.jobId);
      enqueueSnackbar(t("chat:downloadHistory_started"), { variant: "success" });
    } catch (error) {
      enqueueSnackbar(t("chat:downloadHistory_error"), { variant: "error" });
      Sentry.captureException(error);
    }
  };

  return {
    isDownloading: exportConversationHistoryResult.isLoading || !!jobId,
    startDownload,
  };
};

export default useDownloadConversationHistory;
